import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Dropdown, DropdownButton, ListGroup, Spinner } from 'react-bootstrap';
import { useCursor, useUpdateCharGridAtLocs, useSlotStructure, PuzzleInteractionStatus, usePuzzleInteractionStatus, useWordSuggestor, useGhostChars, usePuzzlePreferences, PuzzlePreferenceKey } from './BoardInteractionContext';
import './WordSuggestorPanel.css';
import WordSuggestion from './WordSuggestion';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { completeFillSuggestionWithWordInSlot, getCurrentWordlistObj, getTotalWordCount, useSwdContext, WORD_SUGGESTION_STATUS } from '../../libs/autofillLib';
import { get } from 'aws-amplify/api';
import { useNavigate } from 'react-router-dom';


/*
wordsMap takes the form of a Map with the following format (or null if no slot is selected).
word: {
  score,            // the numeric score of the word according to the wordlist
  isDisplayed,      // true or false, if the word is currently displayed (a subset may be displayed to avoid rendering issues)
  rankPriority,     // a numeric value that determines the word's placement (within the displayed words subset), with higher values appearing toward the top
  status,           // one of the WORD_SUGGESTION_STATUS values indicating whether this word has been evaluated & whether a fill was found
  possibleFill,     // {locsToChange, newChars, essentialLocs} describing the possible fill if this word was selected, or null if not yet evaluated or nonviable
}
*/


/** A side panel to help constructors by suggesting words that can fill the slot they're looking at. */
export default function WordSuggestorPanel({pxHeight=400, ...props}) {
  pxHeight = Math.max(pxHeight || 250, 250);

  // Track any changes in the loaded scoredWordsDatabase
  const { swdVersion, isWordlistLoading } = useSwdContext();
  const [wordlist, setWordlist] = useState(null);
  useEffect(() => {
    setWordlist(getCurrentWordlistObj());     // importantly: this effect is only used for updating the wordlist name/etc. The actual word-loading is handled elsewhere
  }, [swdVersion]);    // update our wordlist anytime the main wordlist is reloaded

  // Alternative wordlists user can switch to
  const [wordlistOptions, setWordlistOptions] = useState(null);
  const navigate = useNavigate();

  // Maintain the optimized words map
  const { wordsMap, displayMoreWords, continuouslyAutofill, fetchDifferentWordlistAndHandleAffairs } = useWordSuggestor();

  const [unfillableWordsExpanded, setUnfillableWordsExpanded] = useState(false);  // whether the displayed words with confirmed no fill are shown
  const [ignoredWordsExpanded, setIgnoredWordsExpanded] = useState(false);        // whether the displayed words that have been ignored due to low scores are shown

  // Get current word slot information (re-calculate at each render)
  const { currentSlotName } = useCursor();
  const slotStructure = useSlotStructure();
  const updateCharGridAtLocs = useUpdateCharGridAtLocs();  // used when a suggestion is clicked to be inserted
  const { puzzleInteractionStatus } = usePuzzleInteractionStatus();
  const { setSuggestedFill } = useGhostChars();
  const { getPuzzlePreference } = usePuzzlePreferences();


  // Mechanism for filling in the suggested fill when it's hovered
  function handleMouseEnter(word) {
    if (continuouslyAutofill && (puzzleInteractionStatus === PuzzleInteractionStatus.EDITABLE_GUEST || puzzleInteractionStatus === PuzzleInteractionStatus.EDITABLE)) {
      if (!/^[a-zA-Z]+$/.test(word)) {
        return;     // ensure it doesn't add any non-alpha characters from the wordlist     // TODO: implement non-alpha character insertion
      }
      if (wordsMap && wordsMap.get(word)) {
        const wordObj = wordsMap.get(word);
        if (wordObj.status === WORD_SUGGESTION_STATUS.FILLABLE) {
          // Add the locs in the slot that aren't already filled in
          const sf = completeFillSuggestionWithWordInSlot(currentSlotName, word, wordObj.possibleFill, slotStructure);
          setSuggestedFill(sf);
        } else {
          setSuggestedFill(null);    // hovering over a non-evaluated or unfillable word should remove the old suggested fill
        }
      }
    }
  }


  // Mechanism for filling in word when it's clicked
  function handleClickOnWord(word) {
    if (puzzleInteractionStatus === PuzzleInteractionStatus.EDITABLE_GUEST || puzzleInteractionStatus === PuzzleInteractionStatus.EDITABLE) {
      word = word.toUpperCase();
      if (!/^[A-Z]+$/.test(word)) {
        return;     // ensure it doesn't add any non-alpha characters from the wordlist     // TODO: implement non-alpha character insertion
      }

      // Fill in locs with letters (rebus-aware)
      const chars = slotStructure.getCharsInSlot(currentSlotName);
      const locs = slotStructure.getLocsInSlot(currentSlotName);
      if (!locs) return;
      const wordArray = word.split('');

      const locsToChange = [];
      const newChars = [];
      let rebusAdjustedIdx = 0;
      for (let slotIdx = 0; slotIdx < locs.length; ++slotIdx) {
        const ch = chars[slotIdx];
        
        if (ch === '') {
          locsToChange.push(locs[slotIdx]);
          newChars.push(wordArray[rebusAdjustedIdx]);
        }

        if (ch.startsWith('r:')) {
          rebusAdjustedIdx += ch.length - 2;
        } else {
          ++rebusAdjustedIdx;
        }
      }

      updateCharGridAtLocs(locsToChange, newChars);
    }
  }


  // A couple of convenience variables to avoid recalculating them a bunch during render
  const unfillableDisplayedWordsMapEntries = wordsMap && Array.from(wordsMap.entries()).filter(([_, obj]) => obj.isDisplayed && obj.status === WORD_SUGGESTION_STATUS.UNFILLABLE);
  const ignoredDisplayedWordsMapEntries = wordsMap && Array.from(wordsMap.entries()).filter(([_, obj]) => obj.isDisplayed && obj.status === WORD_SUGGESTION_STATUS.IGNORED);
  const areThereMoreUndisplayedWords = wordsMap && Array.from(wordsMap.values()).some(o => !o.isDisplayed);
  const currentEvaluatingIndex = wordsMap && Array.from(wordsMap.values())
      .sort((a, b) => b.rankPriority - a.rankPriority)
      .findIndex(obj => obj.isDisplayed && obj.status === WORD_SUGGESTION_STATUS.NOT_EVALUATED);

  return (
    <>
    <Card style={{ height: `${pxHeight}px` }} {...props}>
      {!wordsMap ? (
        <div className='fst-italic text-center text-muted p-3 overflow-auto'>
          <p>
            Welcome to Crossworthy Construct!
          </p>
          <p>
            Click on the grid to get started.
            You can insert letters by typing; add squares with the <strong>space bar</strong> or <strong>period</strong> key.
            If you're new to constructing, we recommend starting with a 5x5 grid.
          </p>
          <p>
            Drop us a line with any suggestions or feedback! Happy constructing!
          </p>
        </div>
      ) : !wordlist ? (
        <span className='fst-italic text-center text-muted m-3'>Wordlist not loaded yet. Sit tight...</span>
      ) : wordsMap.size === 0 ? (
        <span className='fst-italic text-center text-muted m-3'>No matching words in wordlist.</span>
      ) : (
        <Card.Body className='p-0 overflow-auto'>
          <ListGroup variant='flush'>
            {Array.from(wordsMap.entries())
                .filter(([_, suggestionObj]) => suggestionObj.isDisplayed && suggestionObj.status !== WORD_SUGGESTION_STATUS.UNFILLABLE && suggestionObj.status !== WORD_SUGGESTION_STATUS.IGNORED)
                .sort((a, b) => b[1].rankPriority - a[1].rankPriority)
                .map(([word, suggestionObj], idx) => (
              <WordSuggestion
                className='wordSuggestion'
                key={'word-' + idx}
                word={word}
                score={suggestionObj.score}
                showIcon={continuouslyAutofill}
                isEvaluating={idx === currentEvaluatingIndex}
                status={suggestionObj.status}
                onClick={() => handleClickOnWord(word)}
                onMouseEnter={() => handleMouseEnter(word)}
              />
            ))}


            {unfillableDisplayedWordsMapEntries && unfillableDisplayedWordsMapEntries.length > 0 && (
              <ListGroup.Item
                className='py-0 d-flex small text-danger'
                role='button'
                style={{backgroundColor: '#ffe0e0'}}
                onClick={() => setUnfillableWordsExpanded(!unfillableWordsExpanded)}
              >
                No Fill
                <Badge className='mx-2 my-auto' bg='danger'>{unfillableDisplayedWordsMapEntries.length}</Badge>
                {unfillableWordsExpanded ? <FaChevronUp className='my-auto' /> : <FaChevronDown className='my-auto' />}
              </ListGroup.Item>
            )}
            {unfillableWordsExpanded && unfillableDisplayedWordsMapEntries && (
              unfillableDisplayedWordsMapEntries.sort((a, b) => b[1].rankPriority - a[1].rankPriority).map(([word, suggestionObj]) => (
                <WordSuggestion
                  className='wordSuggestion small'
                  key={'word-' + word}
                  word={word}
                  score={suggestionObj.score}
                  showIcon={continuouslyAutofill}
                  status={WORD_SUGGESTION_STATUS.UNFILLABLE}
                  onClick={() => handleClickOnWord(word)}
                  onMouseEnter={() => handleMouseEnter(word)}
                />
              ))
            )}

            
            {ignoredDisplayedWordsMapEntries && ignoredDisplayedWordsMapEntries.length > 0 && (
              <ListGroup.Item
                className='py-0 d-flex small text-secondary grey-on-grey'
                role='button'
                onClick={() => setIgnoredWordsExpanded(s => !s)}
              >
                Ignored (Score &lt; {getPuzzlePreference(PuzzlePreferenceKey.MINIMUM_WORD_SCORE)})
                <Badge className='mx-2 my-auto' bg='secondary'>{ignoredDisplayedWordsMapEntries.length}</Badge>
                {ignoredWordsExpanded ? <FaChevronUp className='my-auto' /> : <FaChevronDown className='my-auto' />}
              </ListGroup.Item>
            )}
            {ignoredWordsExpanded && ignoredDisplayedWordsMapEntries && (
              ignoredDisplayedWordsMapEntries.sort((a, b) => b[1].rankPriority - a[1].rankPriority).map(([word, suggestionObj]) => (
                <WordSuggestion
                  className='wordSuggestion small'
                  key={'word-' + word}
                  word={word}
                  score={suggestionObj.score}
                  showIcon={continuouslyAutofill}
                  status={WORD_SUGGESTION_STATUS.IGNORED}
                  onClick={() => handleClickOnWord(word)}
                  onMouseEnter={() => handleMouseEnter(word)}
                />
              ))
            )}


            {areThereMoreUndisplayedWords && (
              <ListGroup.Item className='py-2 d-flex'>
                <Button
                  className='me-2'
                  style={{ minWidth: '6.1rem' }}
                  variant='secondary'
                  size='sm'
                  onClick={() => displayMoreWords()}
                >More words</Button>
              </ListGroup.Item>
            )}
          </ListGroup>
        </Card.Body>
      )}
    </Card>

    <div className='d-flex gap-3 align-items-start mt-1'>
      {isWordlistLoading ? (
        <div className='fst-italic d-flex gap-2 align-items-center text-secondary'><Spinner variant='secondary' size='sm' />Loading wordlist...</div>
      ) : (
        <>
          {wordlist ? (
            <div>
              <div><strong>Wordlist:</strong> {wordlist.name || '(untitled)'}</div>
              <div className='text-muted small'>{getTotalWordCount()} words</div>
            </div>
          ) : (
            <div className='fst-italic text-danger'>No wordlist selected</div>
          )}

          <DropdownButton
            className='ms-auto py-0'
            variant='outline-primary'
            title='Change wordlist'
            size='sm'
            onClick={async () => {
              if (!wordlistOptions) {
                const wo = await (await get({ apiName: 'wordlists', path: '/wordlists' }).response).body.json();
                setWordlistOptions(wo);
              }
            }}
          >
            {wordlistOptions ? (
              wordlistOptions.filter(w => w.wordlistId !== wordlist?.wordlistId).toSorted((a, b) => b.usedAt - a.usedAt).map(w => (
                <Dropdown.Item key={w.wordlistId} onClick={async () => {
                  await fetchDifferentWordlistAndHandleAffairs(w);
                }}>
                  {w.name || '(untitled wordlist'} {w.isCommunity && <Badge bg='success'>COMMUNITY</Badge>}
                </Dropdown.Item>
              ))
            ) : (
              <Dropdown.Item className='text-muted fst-italic' disabled><Spinner size='sm' />loading...</Dropdown.Item>
            )}

            <Dropdown.Divider />
            <Dropdown.Item onClick={() => {
              if (window.confirm('You are about to navigate away from this crossword page.')) {
                navigate('/wordlists');
              }
            }}>See/manage wordlists...</Dropdown.Item>
          </DropdownButton>
        </>
      )}
    </div>
    </>
  );

}
